import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

export function CoachSettings() {
  const { profile } = useAuth();
  const id = profile?.id;

  const [coachProfile, setCoachProfile] = useState(null);
  const [editCredentials, setEditCredentials] = useState('');
  const [editBio, setEditBio]               = useState('');
  const [saving, setSaving]                 = useState(false);
  const [saved, setSaved]                   = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    if (!id) return;
    supabase.from('coach_profiles').select('*').eq('id', id).maybeSingle()
      .then(({ data }) => {
        setCoachProfile(data ?? null);
        setEditCredentials(data?.credentials ?? '');
        setEditBio(data?.bio ?? '');
      });
  }, [id]);

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    const ext = file.name.split('.').pop();
    const path = `coach_${id}.${ext}`;
    await supabase.storage.from('avatars').upload(path, file, { upsert: true });
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
    const url = `${urlData.publicUrl}?t=${Date.now()}`;
    await supabase.from('coach_profiles').upsert({ id, avatar_url: url }, { onConflict: 'id' });
    setCoachProfile(prev => ({ ...(prev ?? { id }), avatar_url: url }));
    setAvatarUploading(false);
  }

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    const { error } = await supabase.from('coach_profiles').upsert(
      { id, bio: editBio, credentials: editCredentials },
      { onConflict: 'id' }
    );
    if (!error) {
      setCoachProfile(prev => ({ ...(prev ?? { id }), bio: editBio, credentials: editCredentials }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } else {
      console.error('[coach settings] save error:', error.message);
    }
    setSaving(false);
  }

  return (
    <Layout>
      <div style={{ marginBottom: 24 }}>
        <p className="page-eyebrow">Settings</p>
        <h1 className="page-title">My Coach Profile</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--gray-400)' }}>
          Visible to your athletes
        </p>
      </div>

      <div className="section-card" style={{ maxWidth: 600 }}>
        {/* Photo */}
        <div className="section-card-header">
          <span className="section-card-title">Photo</span>
        </div>
        <div style={{ padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 20 }}>
          <label style={{ cursor: 'pointer', flexShrink: 0 }} title="Click to upload photo">
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} disabled={avatarUploading} />
            <div className="profile-avatar" style={{ width: 72, height: 72, fontSize: 22 }}>
              {coachProfile?.avatar_url
                ? <img src={coachProfile.avatar_url} alt={profile?.full_name} />
                : initials(profile?.full_name)}
              <div className="profile-avatar-overlay">{avatarUploading ? '…' : 'Upload'}</div>
            </div>
          </label>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>
              {avatarUploading ? 'Uploading…' : 'Change Photo'}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--gray-400)' }}>
              Upload a photo that athletes will see on their dashboard.
            </p>
          </div>
        </div>

        {/* Credentials & Bio */}
        <div style={{ borderTop: '1px solid var(--gray-100)', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="form-group">
            <label className="form-label">Credentials / Title</label>
            <input
              type="text"
              className="form-input"
              value={editCredentials}
              onChange={e => setEditCredentials(e.target.value)}
              placeholder="e.g. USATF Level 2, SafeSport Certified, 15 yrs coaching"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Bio</label>
            <textarea
              className="form-input"
              value={editBio}
              onChange={e => setEditBio(e.target.value)}
              rows={5}
              placeholder="Tell athletes a bit about your coaching background…"
              style={{ resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ width: 'auto' }}>
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
            {saved && <span style={{ fontSize: 13, color: 'var(--green, #15803d)', fontWeight: 600 }}>Saved ✓</span>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
